import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Clock, Play, Pause, RefreshCw, Calendar, Mail, 
  FileText, CheckCircle, XCircle, AlertTriangle, History, TrendingDown
} from "lucide-react";
import axios from "axios";

const ScheduledTasks = () => {
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningTask, setRunningTask] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        axios.get(`${API}/scheduler/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scheduler/logs?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setStatus(statusRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching scheduler data:", error);
      toast.error("Kunde inte hämta schemaläggningsdata");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleScheduler = async () => {
    try {
      const response = await axios.post(`${API}/scheduler/toggle`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { enabled: !status?.running }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Kunde inte ändra status";
      toast.error(detail);
    }
  };

  const handleRunTask = async (taskName) => {
    setRunningTask(taskName);
    try {
      const response = await axios.post(`${API}/scheduler/run/${taskName}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Kunde inte köra uppgiften";
      toast.error(detail);
    } finally {
      setRunningTask(null);
    }
  };

  const getTaskIcon = (taskName) => {
    switch (taskName) {
      case "recurring_invoices":
        return <RefreshCw className="h-5 w-5" />;
      case "payment_reminders":
        return <Mail className="h-5 w-5" />;
      case "email_invoices":
        return <FileText className="h-5 w-5" />;
      case "monthly_depreciation":
        return <TrendingDown className="h-5 w-5" />;
      default:
        return <Clock className="h-5 w-5" />;
    }
  };

  const getTaskDescription = (taskName) => {
    switch (taskName) {
      case "recurring_invoices":
        return "Genererar nya fakturor för aktiva abonnemang";
      case "payment_reminders":
        return "Skickar påminnelser för förfallna fakturor";
      case "email_invoices":
        return "Skannar e-post för nya leverantörsfakturor";
      case "monthly_depreciation":
        return "Bokför månatliga avskrivningar för anläggningstillgångar";
      default:
        return "";
    }
  };

  const getTaskLabel = (taskName) => {
    switch (taskName) {
      case "recurring_invoices":
        return "Återkommande fakturor";
      case "payment_reminders":
        return "Betalningspåminnelser";
      case "email_invoices":
        return "E-postfakturor";
      case "monthly_depreciation":
        return "Månatliga avskrivningar";
      default:
        return taskName;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString("sv-SE");
  };

  const isAdmin = user?.role === "Administrator";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="scheduled-tasks">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Schemalagda uppgifter</h1>
          <p className="text-slate-500 mt-1">Automatiserade bakgrundsjobb och påminnelser</p>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
              <span className="text-sm text-slate-600">Schemaläggare</span>
              <Switch 
                checked={status?.running}
                onCheckedChange={handleToggleScheduler}
                data-testid="scheduler-toggle"
              />
              <Badge className={status?.running ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}>
                {status?.running ? "Aktiv" : "Pausad"}
              </Badge>
            </div>
          )}
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Uppdatera
          </Button>
        </div>
      </div>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {status?.jobs?.map((job) => (
          <Card key={job.id} className="card-base" data-testid={`job-${job.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${status?.running ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                  {getTaskIcon(job.id)}
                </div>
                <Badge variant="outline" className="text-xs">
                  {job.trigger}
                </Badge>
              </div>
              
              <h3 className="font-semibold text-slate-900 mb-1">{job.name}</h3>
              <p className="text-sm text-slate-500 mb-4">{getTaskDescription(job.id)}</p>
              
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-400">
                  <Clock className="inline h-3 w-3 mr-1" />
                  Nästa: {formatDateTime(job.next_run)}
                </div>
                
                {isAdmin && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleRunTask(job.id)}
                    disabled={runningTask === job.id}
                    data-testid={`run-${job.id}`}
                  >
                    {runningTask === job.id ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <Play className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Manual Run Cards */}
      {isAdmin && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Play className="h-5 w-5" />
              Kör manuellt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={() => handleRunTask("recurring_invoices")}
                disabled={!!runningTask}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Generera abonnemangsfakturor
              </Button>
              <Button 
                onClick={() => handleRunTask("payment_reminders")}
                disabled={!!runningTask}
                variant="outline"
              >
                <Mail className="mr-2 h-4 w-4" />
                Skicka betalningspåminnelser
              </Button>
              <Button 
                onClick={() => handleRunTask("email_invoices")}
                disabled={!!runningTask}
                variant="outline"
              >
                <FileText className="mr-2 h-4 w-4" />
                Skanna e-postfakturor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logs */}
      <Card className="card-base" data-testid="task-logs">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Körhistorik
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {logs.map((log, index) => (
                <div key={index} className="p-4 hover:bg-slate-50" data-testid={`log-${index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${log.status === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {log.status === 'success' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{getTaskLabel(log.task)}</p>
                        <p className="text-sm text-slate-500">
                          {log.result || log.error || "-"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={log.status === 'success' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                        {log.status === 'success' ? 'Lyckades' : 'Misslyckades'}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatDateTime(log.run_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <History className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Ingen körhistorik ännu</p>
              <p className="text-sm mt-1">Schemalagda uppgifter kommer att loggas här</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Om schemalagda uppgifter</p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Återkommande fakturor:</strong> Körs kl 06:00 varje dag<br/>
                <strong>Betalningspåminnelser:</strong> Körs kl 09:00 varje dag (kräver Resend API-nyckel)<br/>
                <strong>E-postfakturor:</strong> Körs varannan timme (kräver IMAP-konfiguration)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScheduledTasks;
